import type { AnyRouter } from "@tanstack/react-router";

type UnknownRecord = Record<string, unknown>;
type SentryModule = typeof import("@sentry/react");

const SCRUB_KEY_PATTERN =
	/^(password|secret|token|api[-_]?key|authorization|cookie)$/i;

const MAX_SCRUB_DEPTH = 5;

function scrub(value: unknown, depth: number): unknown {
	if (depth > MAX_SCRUB_DEPTH || value === null || value === undefined) {
		return value;
	}
	if (Array.isArray(value)) {
		return value.map((item) => scrub(item, depth + 1));
	}
	if (typeof value !== "object") {
		return value;
	}
	const source = value as UnknownRecord;
	const out: UnknownRecord = {};
	for (const [key, entry] of Object.entries(source)) {
		if (SCRUB_KEY_PATTERN.test(key)) {
			out[key] = "[Filtered]";
			continue;
		}
		out[key] = scrub(entry, depth + 1);
	}
	return out;
}

export function scrubSensitive<T>(input: T): T {
	return scrub(input, 0) as T;
}

function readEnv(key: string): string | undefined {
	try {
		const meta = import.meta as unknown as { env?: Record<string, unknown> };
		const value = meta.env?.[key];
		return typeof value === "string" && value !== "" ? value : undefined;
	} catch {
		return;
	}
}

function readMode(): string {
	try {
		const meta = import.meta as unknown as { env?: { MODE?: string } };
		return meta.env?.MODE ?? "production";
	} catch {
		return "production";
	}
}

function resolveDsn(): string | undefined {
	const mode = readMode();
	if (mode === "production") {
		return readEnv("VITE_SENTRY_DSN");
	}
	return readEnv("VITE_SENTRY_DSN_DEV") ?? readEnv("VITE_SENTRY_DSN");
}

function resolveTracesSampleRate(): number {
	const raw = readEnv("VITE_SENTRY_TRACES_SAMPLE_RATE");
	if (!raw) {
		return 0.1;
	}
	const parsed = Number.parseFloat(raw);
	return Number.isFinite(parsed) ? parsed : 0.1;
}

let initialized = false;
let sentryModule: SentryModule | null = null;
let sentryLoadPromise: Promise<SentryModule> | null = null;
let tracingRouter: AnyRouter | null = null;
let tracingWired = false;

export function initSentry(): boolean {
	if (initialized) {
		return true;
	}
	const dsn = resolveDsn();
	if (!dsn) {
		return false;
	}
	if (!sentryLoadPromise) {
		sentryLoadPromise = import("@sentry/react");
	}
	sentryLoadPromise.then((Sentry) => {
		if (initialized) {
			return;
		}
		sentryModule = Sentry;
		Sentry.init({
			dsn,
			environment: readMode(),
			tracesSampleRate: resolveTracesSampleRate(),
			replaysSessionSampleRate: 0.0,
			replaysOnErrorSampleRate: 1.0,
			sendDefaultPii: false,
			initialScope: {
				tags: { errorSource: "client" },
			},
			integrations: [
				Sentry.browserTracingIntegration(),
				Sentry.replayIntegration({
					maskAllText: true,
					blockAllMedia: true,
				}),
			],
			beforeSend(event) {
				if (event.request) {
					if (event.request.headers) {
						event.request.headers = scrubSensitive(event.request.headers);
					}
					if (event.request.cookies) {
						event.request.cookies = scrubSensitive(event.request.cookies);
					}
				}
				if (event.contexts) {
					event.contexts = scrubSensitive(event.contexts);
				}
				if (event.tags) {
					event.tags = scrubSensitive(event.tags);
				}
				if (event.extra) {
					event.extra = scrubSensitive(event.extra);
				}
				return event;
			},
		});
		initialized = true;
		if (tracingRouter) {
			wireRouterTracing(tracingRouter);
		}
	});
	return true;
}

export function isSentryActive(): boolean {
	return initialized && sentryModule?.getClient() !== undefined;
}

export function wireRouterTracing(router: AnyRouter): void {
	tracingRouter = router;
	if (!(isSentryActive() && sentryModule) || tracingWired) {
		return;
	}
	const Sentry = sentryModule;
	const client = Sentry.getClient();
	if (!client) {
		return;
	}
	tracingWired = true;
	router.subscribe("onResolved", (event) => {
		try {
			Sentry.startBrowserTracingNavigationSpan(client, {
				name: event.toLocation.pathname,
				op: "navigation",
			});
		} catch {
			// no-op: navigation tracing is best-effort
		}
	});
}
